// The six racing cats plus the ham and catnip on the track: the world's entry point for cats.
//
//   const cats = await CatField.create(renderer, quality.settings);
//   scene.add(cats.group);
//   cats.setCamera(camera);                    // detail by distance, culling, settled cats look at it
//   loop: cats.update(snapshot, dt, timeS);
//   quality.onChange((s) => cats.setTier(s));
//
// The catnip trip takes a cat over with `cats.actor(id).override = {...}` and hands it back with null.
import { type Camera, Frustum, Group, Matrix4, Sphere, Vector3, type Scene, type WebGLRenderer } from 'three';
import { CAT_DISPLAY_SCALE, CATS } from '../../data/cats';
import type { CatId, RaceSnapshot } from '../../race/types';
import type { TierSettings } from '../quality';
import { CatActor } from './catActor';
import { type BaseCat, CatModel, loadBaseCat } from './catFactory';
import { podiumPlaces, type PodiumPlace } from './podiumIdle';
import { CatnipProp, HamProp } from './props';

/** Camera distances (m) where a cat's detail steps down. */
export const LOD_STEPS = { fullFur: 7, halfFur: 16, lowMesh: 32, noFur: 70 } as const;

/** Fur shells and mesh for a distance. Pure. */
export function detailFor(distance: number, maxShells: number): { lod: 0 | 1; shells: number } {
  if (maxShells === 0) return { lod: distance < LOD_STEPS.lowMesh ? 0 : 1, shells: 0 };
  if (distance < LOD_STEPS.fullFur) return { lod: 0, shells: maxShells };
  if (distance < LOD_STEPS.halfFur) return { lod: 0, shells: Math.ceil(maxShells / 2) };
  if (distance < LOD_STEPS.lowMesh) return { lod: 0, shells: Math.ceil(maxShells / 4) };
  if (distance < LOD_STEPS.noFur) return { lod: 1, shells: Math.min(4, maxShells) };
  return { lod: 1, shells: 0 };
}

const _frustum = new Frustum();
const _pv = new Matrix4();
const _sphere = new Sphere(new Vector3(), 1);
const _cam = new Vector3();

export class CatField {
  readonly group = new Group();
  readonly actors: CatActor[];
  readonly ham = new HamProp();
  readonly catnip = new CatnipProp();
  private camera: Camera | null = null;
  private maxShells: number;
  private podiumFor: RaceSnapshot['results'] | null = null;
  private places = new Map<CatId, PodiumPlace>();

  /** Loads the base cat (unless given) and builds all six cats. */
  static async create(renderer: WebGLRenderer, tier: TierSettings, base?: BaseCat): Promise<CatField> {
    return new CatField(renderer, tier, base ?? await loadBaseCat());
  }

  constructor(renderer: WebGLRenderer, tier: TierSettings, readonly base: BaseCat) {
    this.group.name = 'cats';
    // Every cat gets the High tier's shells built once; the tier only changes how many are drawn.
    this.maxShells = tier.furShells;
    const built = Math.max(24, tier.furShells);
    this.actors = CATS.map((def) => new CatActor(def, new CatModel(renderer, base, def, built), base));
    for (const a of this.actors) this.group.add(a.group);
    this.group.add(this.ham.group, this.catnip.group);
  }

  actor(id: CatId): CatActor {
    const a = this.actors.find((x) => x.def.id === id);
    if (!a) throw new Error(`cats: no actor "${id}"`);
    return a;
  }

  setCamera(camera: Camera): void { this.camera = camera; }

  setTier(tier: TierSettings): void { this.maxShells = tier.furShells; }

  /** Compiles every cat shader up front so the first race frame does not stall. */
  async compile(renderer: WebGLRenderer, camera: Camera, scene: Scene): Promise<void> {
    await renderer.compileAsync(this.group, camera, scene);
  }

  update(snap: RaceSnapshot, dt: number, timeS: number): void {
    const winner = snap.phase === 'finished' && snap.results ? snap.results.find((r) => r.place === 1 && !r.dsq)?.id ?? null : null;
    const look = this.camera ? this.camera.getWorldPosition(_cam) : null;
    // the results put the cats on the podium; the camera cuts there at the same moment
    const results = snap.phase === 'finished' ? snap.results ?? null : null;
    if (results !== this.podiumFor) { this.podiumFor = results; this.places = results ? podiumPlaces(results) : new Map(); }
    for (const c of snap.cats) {
      const a = this.actors[c.lane];
      if (!a) continue;
      a.update(c, { phase: snap.phase, dt, timeS, winner: winner === c.id, look, podium: this.places.get(c.id) ?? null });
    }

    // the ham disappears into the mouth of the cat eating it
    const eater = snap.ham ? snap.cats.find((c) => c.eating && c.lane === snap.ham!.lane) : undefined;
    this.ham.update(snap.ham, !!eater, dt);
    this.catnip.update(snap.phase === 'running' ? snap.catnip : null, dt);

    // detail by distance, and skip cats outside the view
    if (!this.camera) return;
    const cam = this.camera;
    cam.updateMatrixWorld();
    _pv.multiplyMatrices(cam.projectionMatrix, cam.matrixWorldInverse);
    _frustum.setFromProjectionMatrix(_pv);
    for (const a of this.actors) {
      a.bodyCentre(_sphere.center);
      _sphere.radius = 1.2 * CAT_DISPLAY_SCALE;
      const inView = _frustum.intersectsSphere(_sphere);
      a.model.root.visible = inView;
      if (!inView) continue;
      const d = _sphere.center.distanceTo(look!);
      const want = detailFor(d, this.maxShells);
      if (want.lod !== a.model.currentLod) a.model.setLod(want.lod);
      if (want.shells !== a.model.shellCount) a.model.setShells(want.shells);
    }
  }

  /**
   * Shows the cats a second camera (the trip's live-race picture) sees that update() culled for the
   * main camera, with detail for that camera's distance. Returns the call that hides them again.
   */
  revealFor(camera: Camera): () => void {
    camera.updateMatrixWorld();
    _pv.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    _frustum.setFromProjectionMatrix(_pv);
    const look = camera.getWorldPosition(_cam);
    const shown: CatActor[] = [];
    for (const a of this.actors) {
      if (a.model.root.visible) continue;
      a.bodyCentre(_sphere.center);
      _sphere.radius = 1.2 * CAT_DISPLAY_SCALE;
      if (!_frustum.intersectsSphere(_sphere)) continue;
      const want = detailFor(_sphere.center.distanceTo(look), this.maxShells);
      if (want.lod !== a.model.currentLod) a.model.setLod(want.lod);
      if (want.shells !== a.model.shellCount) a.model.setShells(want.shells);
      a.model.root.visible = true;
      shown.push(a);
    }
    return () => { for (const a of shown) a.model.root.visible = false; };
  }

  dispose(): void {
    for (const a of this.actors) a.model.dispose();
  }
}
